import Vue from "vue";
import Vuex from "vuex";

// store modules
import webtv from "./modules/webtv";
import news from "./modules/news";
import signals from "./modules/signals";
import webinars from "@/store/modules/webinars";
import ebooks from "@/store/modules/ebooks";
import loginOrRegister from "./modules/loginOrRegister";
import symbolsSocket from "@/store/modules/symbolsSocket";
import apiRequest from "../services/apiRequest";
import {WebsiteInfo} from "../utils/general";
import VueCookies from "vue-cookies";

Vue.use(VueCookies);
Vue.use(Vuex);

export default new Vuex.Store({
    strict: process.env.NODE_ENV !== "production",

    modules: {
        webtv,
        news,
        webinars,
        ebooks,
        signals,
        loginOrRegister,
        symbolsSocket
    },

    state: {
        lang: "",
        brand: "al",
        domain: "alvexo",
        showNotificationModal: false,
        isLoggedIn: null,
        accountId: "",
        account: null,
        webSiteDictionary: {},
        symbolsData: {},
        registrationStep: 1,
        displayedAuthForm: "registration",
        userFirstName: "",
        membershipActive: null,
        webInfo: null,
        platformUrl: "",
        traderDictionary: {},
        itemRelatedAssets: [],
        displayMobileSharePopup: false,
        pendingApiCallsCount: 0,
        showLoaderUntilUpdateUserStatusReturns: false,
        userStatusReturned: false,
        regulation: "vpr",
        pathswhichRequiresWaitingForUserStatus: [
            "financial-tv-item",
            "trading-signals",
            "news-item",
            "webinars-item",
            "ebooks-item"
        ],
        componentMountLoader: true //make sure the loader will appears until the main component at certain route has mounted, even if there are no asyn calls
    },

    getters: {
        t: state => key => {
            return state.webSiteDictionary[key] ? state.webSiteDictionary[key] : key;
        },
        traderDict: state => key => {
            return state.traderDictionary[key] ? state.traderDictionary[key] : "";
        },
        userLoginLink(state) {
            let aidParams = state.accountId != "" ? "?aid=" + state.accountId : "";
            return state.platformUrl.login + aidParams;
        },
        userSignupLink(state) {
            return state.platformUrl.sign_up;
        },
        isLoaderActive(state) {
            return state.pendingApiCallsCount !== 0 || state.componentMountLoader;
        },
        chartInstrumentDeepLink: state => instrumentName => {
            return `${state.platformUrl.trader_link}dashboard?lang=${state.lang}&deeplink=chart&product=${instrumentName}`;
        },
        userPlatformSignalInfoLinkBase: state => (signal, type) => {
            const additionalPath = `dashboard?lang=${state.lang}&deeplink=signal-info&product=`;
            return (
                state.platformUrl.trader_link +
                additionalPath +
                signal.symbol +
                "&signal-id=" +
                signal.id +
                "&signal-action=" +
                type
            );
        },
        getSymbolImageSrc: (state, getters) => symbolName => {
            return state.symbolsData[symbolName]
                ? getters.baseImgUrl + state.symbolsData[symbolName].URL
                : "";
        },
        baseImgUrl(state) {
            //it cant use the dynamic trader link which returns from the server because it dosen't fit the images location on the server
            return `https://trader.${
                state.domain
            }.${state.webInfo.getHostnameDomain()}/`;
        },
        alvexoWebsite() {
            let url = window.location.origin.replace(/dev1|dev/, "stage");
            return url + "/";
        },
        baseApiUrl(state) {
            return (resource) => {
                return state.webInfo.getWebapiUrl(resource) + "v3/";
            };
        },
        baseNewApiUrl(state) {
            let url = state.webInfo.getFullHostWithProtocol() + "/services";
            return url.replace(/dev1|dev/, "stage");
        },
        //important note: This getter is currently unnecessary because membership active is define only when user logged in.
        //it is defined in order to adapt to future code changes, for case memberhsipActive will somehow be setted seperately from the logged in status
        loggedAndActive(state) {
            return state.isLoggedIn && state.membershipActive;
        }
    },

    mutations: {
        setLang(state, lang) {
            state.lang = lang;
        },
        setShowNotificationModal(state, modalDisplayStatus) {
            state.showNotificationModal = modalDisplayStatus;
        },
        setLoginStatus(state, isLogged) {
            state.isLoggedIn = isLogged;
        },
        setRegistrationStep(state, formStepNumber) {
            state.registrationStep = formStepNumber;
        },
        setdisplayedAuthForm(state, registrationOrLogin) {
            state.displayedAuthForm = registrationOrLogin;
        },
        setAccount(state, account) {
            state.account = account;
        },
        setDictionary(state, data) {
            state.webSiteDictionary = data;
        },
        setTraderDictionary(state, data) {
            state.traderDictionary = data;
        },
        setSymbolsData(state, data) {
            state.symbolsData = data;
        },
        setUserFirstName(state, firstName) {
            state.userFirstName =
                firstName.charAt(0).toUpperCase() + firstName.slice(1);
        },
        setMembershipActive(state, isMembershipActive) {
            state.membershipActive = isMembershipActive;
        },
        setAccountId(state, aid) {
            state.accountId = aid;
        },
        updateWebsiteInfo(state) {
            state.webInfo = WebsiteInfo();
        },
        getRegulationFromBackend(state) {
            const elemContainingClass = document.body;
            const informativeRegulationClass = elemContainingClass.className.split(' ').find(cl => cl.includes("brand"));
            const regulationFromClass = informativeRegulationClass && informativeRegulationClass.split('-')[1]; //getting the regulation according to the backend logic. not ideal, but this was the solution offered by the backend developer to avoid exposing the logic
            if (regulationFromClass) {
                state.regulation = regulationFromClass;
            }
        },
        setPlatformUrl(state, url) {
            state.platformUrl = url;
        },
        setItemRelatedAssets(state, relatedAssets) {
            state.itemRelatedAssets = relatedAssets;
        },
        setDisplayMobileSharePopup(state, displayStatus) {
            state.displayMobileSharePopup = displayStatus;
        },
        incrementPendingApiCallsCount(state) {
            state.pendingApiCallsCount++;
        },
        decrementPendingApiCallsCount(state) {
            state.pendingApiCallsCount--;
        },
        setShowLoaderUntilUpdateUserStatusReturns(state, newStatus) {
            state.showLoaderUntilUpdateUserStatusReturns = newStatus;
        },
        activateUserStatusReturned(state) {
            state.userStatusReturned = true;
        },
        hideComponentMountLoader(state) {
            //only false setting is possible, in order to emphazie the porpuse of that flag
            state.componentMountLoader = false;
        }
    },

    actions: {
        updateAppLang({commit, dispatch}, newLang) {
            commit("setLang", newLang);
            dispatch("getPlatformUrl");
            dispatch("getWebDictionary");
        },
        async getWebDictionary({getters, commit, state}) {
            let json = await apiRequest.get(
                getters.alvexoWebsite +
                `resources/${state.lang}/dictionary`
            );

            commit("setDictionary", json);
        },

        getInfoFromWebsite({dispatch}, type) {
            let actionName;
            switch (type) {
                case "symbols":
                    actionName = "getSymbolsDetails";
                    break;
                case "traderDictionary":
                    actionName = "getTraderDictionary";
                    break;
                default:
                    break;
            }
            if (process.env.NODE_ENV === "development" && !(window.Website && window.Website.WebApiData)) {
                //at develop environment, the "Website.WebApiData" sometimes is not yet loaded when this function run
                return new Promise((resolve) => {
                    const intervalId = setInterval(function () {
                        if (window.Website && window.Website.WebApiData) {
                            clearInterval(intervalId);
                            dispatch(actionName).then(res => {
                                resolve(res)
                            });
                        }
                    }, 100)
                })
            } else {
                return dispatch(actionName).then(res => {
                    return res;
                });
            }
        },

        async getTraderDictionary({commit}) {
            window.Website.WebApiData.waitForCallback("dictionary", res => {
                console.log("res", res.resource);
                commit("setTraderDictionary", res.resource);
            });
        },
        succesfullyLoggedIn({commit}, data) {
            console.log("is logged");
            commit("setAccount", data);
            commit("setLoginStatus", true);
            commit("setAccountId", data.Account.pay.aud);
            commit("setMembershipActive", !data.Account.pay.is_expired);
        },
        exsitingAccountNotLogged({commit}) {
            console.log("need to login");
            commit("setLoginStatus", false);
            commit("setdisplayedAuthForm", "login");
        },
        notRegistered({commit}) {
            console.log("not registered");
            commit("setLoginStatus", null);
            commit("setdisplayedAuthForm", "registration");
        },

        updateUserStatus({state, commit, dispatch}, loginData) {
            if (loginData.isLogged) {
                dispatch("succesfullyLoggedIn", loginData);
            } else {
                if (Vue.$cookies.get("cguid") !== null) {
                    dispatch("exsitingAccountNotLogged");
                } else {
                    dispatch("notRegistered");
                }
            }
            commit("activateUserStatusReturned");
            if (state.showLoaderUntilUpdateUserStatusReturns) {
                commit("decrementPendingApiCallsCount");
            }
        },
        activateShowLoaderUntilUpdateUserStatusReturns({commit}) {
            commit("incrementPendingApiCallsCount");
            commit("setShowLoaderUntilUpdateUserStatusReturns", true);
        },
        getSymbolsDetails({commit}) {
            commit("incrementPendingApiCallsCount");
            return new Promise((resolve) => {
                window.Website.WebApiData.waitForCallback("symbols", function (res) {
                    commit("setSymbolsData", res.resource);
                    commit("decrementPendingApiCallsCount");
                    resolve(res);
                })
            })
        },
        async getPlatformUrl({commit, getters, state}) {
            apiRequest
                .get(getters.alvexoWebsite + `resources/${state.lang}/platform_urls`)
                .then(function (json) {
                    commit("setPlatformUrl", json);
                });
        }
    }
});
